import React, { useContext } from 'react';
import { motion } from 'framer-motion';
import { XIcon, CheckCircleIcon, AlertCircleIcon, AlertTriangleIcon, InfoIcon } from 'lucide-react';
import { NotificationProps, NotificationType } from '../../context/NotificationContext';
import { LanguageContext } from '../../App';
interface ToastProps extends Omit<NotificationProps, 'variant'> {
  onDismiss: () => void;
}
export const Toast: React.FC<ToastProps> = ({
  title,
  message,
  type = 'info',
  onDismiss,
  onAction,
  actionText,
  position,
  isOpen = true
}) => {
  const {
    language
  } = useContext(LanguageContext);
  const getIcon = () => {
    switch (type) {
      case 'success':
        return <CheckCircleIcon className="w-5 h-5" data-id="element-762" />;
      case 'error':
        return <AlertCircleIcon className="w-5 h-5" data-id="element-763" />;
      case 'warning':
        return <AlertTriangleIcon className="w-5 h-5" data-id="element-764" />;
      case 'info':
        return <InfoIcon className="w-5 h-5" data-id="element-765" />;
      default:
        return null;
    }
  };
  const getTypeStyles = (): {
    bg: string;
    text: string;
    icon: string;
  } => {
    switch (type) {
      case 'success':
        return {
          bg: 'bg-green-500',
          text: 'text-white',
          icon: 'text-white'
        };
      case 'error':
        return {
          bg: 'bg-red-500',
          text: 'text-white',
          icon: 'text-white'
        };
      case 'warning':
        return {
          bg: 'bg-yellow-500',
          text: 'text-white',
          icon: 'text-white'
        };
      case 'info':
        return {
          bg: 'bg-congress-blue-500',
          text: 'text-white',
          icon: 'text-white'
        };
      case 'neutral':
        return {
          bg: 'bg-gray-600',
          text: 'text-white',
          icon: 'text-white'
        };
      default:
        return {
          bg: 'bg-gray-600',
          text: 'text-white',
          icon: 'text-white'
        };
    }
  };
  const styles = getTypeStyles();
  const getAnimationProps = () => {
    // Different animations based on position
    if (position?.includes('bottom')) {
      return {
        initial: {
          opacity: 0,
          y: 50,
          scale: 0.8
        },
        animate: {
          opacity: 1,
          y: 0,
          scale: 1
        },
        exit: {
          opacity: 0,
          y: 20,
          scale: 0.8
        }
      };
    }
    return {
      initial: {
        opacity: 0,
        y: -50,
        scale: 0.8
      },
      animate: {
        opacity: 1,
        y: 0,
        scale: 1
      },
      exit: {
        opacity: 0,
        y: -20,
        scale: 0.8
      }
    };
  };
  if (!isOpen) return null;
  return <motion.div className={`${styles.bg} ${styles.text} rounded-md shadow-lg pointer-events-auto max-w-md w-full overflow-hidden`} {...getAnimationProps()} transition={{
    duration: 0.2
  }} data-id="element-766">
      <div className="flex items-center justify-between p-4" data-id="element-767">
        <div className="flex items-center" data-id="element-768">
          <span className={`mr-3 ${styles.icon}`} data-id="element-769">{getIcon()}</span>
          <p className="font-medium" data-id="element-770">{title}</p>
        </div>
        <button onClick={onDismiss} className={`${styles.icon} hover:opacity-70 focus:outline-none focus:ring-2 focus:ring-white focus:ring-opacity-50 rounded-full p-1`} aria-label={language === 'en' ? 'Close' : 'إغلاق'} data-id="element-771">
          <XIcon className="w-4 h-4" data-id="element-772" />
        </button>
      </div>
      {(message || actionText) && <div className="px-4 pb-4 pt-0" data-id="element-773">
          {message && <p className="text-sm opacity-90 mb-2" data-id="element-774">{message}</p>}
          {actionText && onAction && <button onClick={onAction} className="text-sm underline hover:opacity-80 focus:outline-none focus:ring-2 focus:ring-white focus:ring-opacity-50 rounded px-1" data-id="element-775">
              {actionText}
            </button>}
        </div>}
    </motion.div>;
};